import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import PageBanner from "@/components/PageBanner";
import SectionHeading from "@/components/SectionHeading";
import { FileCheck, Search, Droplets, FlaskConical, Shield } from "lucide-react";
import qualityImage from "@/assets/quality-control.webp";

const Quality = () => {
  return (
    <div>
      <PageBanner
        title="Quality & Sourcing"
        subtitle="At MM2A Tunisian Export, sourcing is not only about availability. It is about selecting the right profile, understanding the intended use and building a commercially relevant offer with export suitability in mind."
        backgroundImage={qualityImage}
      />

      {/* Selection Process */}
      <section className="section-padding">
        <div className="container-max grid md:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeading title="Selection Process" centered={false} />
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Our sourcing approach is based on understanding the product before presenting it commercially.</p>
              <p>We work with selected supply partners and evaluate available lots according to profile, origin, intended use and buyer requirements.</p>
              <p>We do not aggregate random supply. Every lot is assessed before being proposed.</p>
            </div>
          </div>
          <div className="flex justify-center">
            <div className="w-32 h-32 rounded-full bg-muted flex items-center justify-center">
              <Search className="w-16 h-16 text-olive" />
            </div>
          </div>
        </div>
      </section>

      {/* Regional Understanding */}
      <section className="section-padding bg-muted">
        <div className="container-max">
          <SectionHeading title="Regional Understanding" subtitle="Because we work across northern, central and southern Tunisia, we are able to access a broader sourcing base." />
          <div className="grid md:grid-cols-3 gap-8 mt-8">
            {[
              { region: "North Tunisia", desc: "Greener, more intense, Chetoui-dominant" },
              { region: "Central Tunisia", desc: "Flexible sourcing base, mixed profiles, strong volume" },
              { region: "South Tunisia", desc: "Smoother, Chemlali-dominant, suited to large-scale supply" },
            ].map((r) => (
              <div key={r.region} className="bg-card p-8 rounded-lg border border-border text-center">
                <h3 className="font-heading text-xl font-semibold mb-3 text-foreground">{r.region}</h3>
                <p className="text-muted-foreground text-sm">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Profile Matching */}
      <section className="section-padding">
        <div className="container-max grid md:grid-cols-2 gap-12 items-center">
          <div className="flex justify-center md:order-2">
            <div className="w-32 h-32 rounded-full bg-muted flex items-center justify-center">
              <Droplets className="w-16 h-16 text-olive" />
            </div>
          </div>
          <div>
            <SectionHeading title="Profile Matching" centered={false} />
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Our objective is not simply to offer olive oil, but to align the right profile with the right commercial need.</p>
              <p>This may involve varietal selection, regional sourcing logic or blend-oriented preparation depending on the buyer's target.</p>
              <p>We discuss the requirement first, then propose the most relevant supply option.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Sample Evaluation */}
      <section className="section-padding bg-muted">
        <div className="container-max grid md:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeading title="Sample Evaluation" centered={false} />
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>We believe that serious cooperation begins with product understanding.</p>
              <p>For this reason, sample evaluation is a normal part of our process before any final commercial alignment.</p>
              <p>Our objective is to keep discussions grounded in the real product — not abstract specifications.</p>
            </div>
          </div>
          <div className="flex justify-center">
            <div className="w-32 h-32 rounded-full bg-card flex items-center justify-center">
              <FlaskConical className="w-16 h-16 text-olive" />
            </div>
          </div>
        </div>
      </section>

      {/* Traceability & Documentation */}
      <section className="section-padding">
        <div className="container-max">
          <SectionHeading title="Traceability & Documentation" />
          <div className="max-w-3xl mx-auto text-center mb-10">
            <p className="text-muted-foreground leading-relaxed">
              A professional export operation requires more than product availability. It also requires clarity, traceability and reliable documentation. MM2A works with a serious export mindset focused on buyer confidence, operational consistency and clear commercial communication.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {["Certificate of Origin", "COA — Certificate of Analysis", "Health Certificate", "Phytosanitary Certificate", "Additional certifications"].map((d) => (
              <div key={d} className="bg-muted p-4 rounded-lg text-center">
                <FileCheck className="w-8 h-8 text-olive mx-auto mb-2" />
                <p className="text-sm font-medium text-foreground">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Built for Buyer Confidence */}
      <section className="bg-primary text-primary-foreground section-padding">
        <div className="container-max text-center">
          <Shield className="w-16 h-16 text-gold mx-auto mb-6" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">Built for Buyer Confidence</h2>
          <p className="text-primary-foreground/80 text-lg max-w-2xl mx-auto leading-relaxed">
            Our role is to make sourcing discussions clearer, more practical and more reliable for professional buyers looking for Tunisian olive oil supply.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Quality;
